import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupMenuEngineeringRead — AI strategic read lifecycle hook for the
// cross-unit menu engineering surface (Ship 5). The group menu-engineering
// analogue of useRollupBrief.
//
//   1. On mount (and when groupId resolves), look up the row in
//      ai_group_briefs for (group_id, tab_key='menu-engineering') where
//      generated_at > now() - 6h. Cache hit → phase='ready' (or 'empty'
//      when payload._empty). Miss → phase='generating', fire the edge
//      fn, which writes the cache row and returns the payload.
//   2. regenerate({ bypassCache }) — explicit refresh past the window
//      (the Refresh control). Re-calls the edge fn.
//   3. inFlightRef synchronous double-fire guard — set before the first
//      await, cleared in finally. Stops two quick Refresh clicks from
//      draining model spend.
//   4. Calm deterministic fallback on error/empty — never the raw error.
//
// DECOUPLED from useGroupMenuEngineering (the scatter data): the scatter
// must render even while this read is generating / empty / failed.
//
// Body is just { group_id } — the edge fn assembles the snapshot
// server-side and its group-ownership gate is the tenant boundary.

const TAB_KEY = 'menu-engineering'
const CACHE_WINDOW_HOURS = 6

export function useGroupMenuEngineeringRead(groupId) {
  // --- All hooks declared BEFORE any conditional return (React #310). ---
  const [phase, setPhase]       = useState('idle')   // idle | generating | ready | empty | error
  const [read, setRead]         = useState(null)
  const [error, setError]       = useState(null)
  const [generatedAt, setGenAt] = useState(null)

  const inFlightRef = useRef(false)

  const lookupAndMaybeGenerate = useCallback(async ({ bypassCache = false } = {}) => {
    if (!groupId) return
    if (inFlightRef.current) return

    setError(null)

    // 1. Cache lookup (skipped on regenerate).
    if (!bypassCache) {
      const cutoff = new Date(Date.now() - CACHE_WINDOW_HOURS * 60 * 60 * 1000).toISOString()
      const { data: rows, error: cacheErr } = await supabase
        .from('ai_group_briefs')
        .select('generated_at, payload')
        .eq('group_id', groupId)
        .eq('tab_key', TAB_KEY)
        .gte('generated_at', cutoff)
        .order('generated_at', { ascending: false })
        .limit(1)

      if (!cacheErr && rows && rows.length > 0) {
        const row = rows[0]
        setRead(row.payload || null)
        setGenAt(row.generated_at)
        setPhase(row.payload && row.payload._empty ? 'empty' : 'ready')
        return
      }
      if (cacheErr) {
        // eslint-disable-next-line no-console
        console.warn('[useGroupMenuEngineeringRead] cache read failed', cacheErr)
      }
    }

    // 2. Cache miss → call the edge fn. Claim the in-flight slot
    //    synchronously BEFORE the await.
    inFlightRef.current = true
    setPhase('generating')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/generate-group-menu-engineering-read`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        // No snapshot — assembly is server-side. The server verifies
        // group ownership against the JWT before trusting group_id.
        body: JSON.stringify({ group_id: groupId }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        setPhase('error')
        setError(data.message || "Couldn't analyze your group menu yet — tap to try again.")
        return
      }
      setRead(data || null)
      setGenAt(new Date().toISOString())
      setPhase(data?._empty ? 'empty' : 'ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useGroupMenuEngineeringRead] edge fn call failed', e)
      // Deterministic calm fallback. Never the raw error.
      setPhase('error')
      setError("Couldn't analyze your group menu yet — tap to try again.")
    } finally {
      inFlightRef.current = false
    }
  }, [groupId])

  // Initial load — kick off once groupId resolves.
  useEffect(() => {
    if (!groupId) return
    lookupAndMaybeGenerate({ bypassCache: false })
  }, [groupId, lookupAndMaybeGenerate])

  const regenerate = useCallback(({ bypassCache = true } = {}) => {
    if (inFlightRef.current) return
    return lookupAndMaybeGenerate({ bypassCache })
  }, [lookupAndMaybeGenerate])

  return useMemo(
    () => ({ phase, read, error, generatedAt, regenerate }),
    [phase, read, error, generatedAt, regenerate],
  )
}
